import React, { memo, useCallback } from 'react';

import PropTypes from 'prop-types';
import cn from 'classnames';
import { InputControlLabel } from '@picsio/ui';

import localization from '../../shared/strings';
import DetailsBlock from '../DetailsBlock';
import PricingField from './PricingField';
import Tooltip from '../Tooltip/Tooltip';
import ReadOnlyFieldTooltip from './helpers/ReadOnlyFieldTooltip';
import buildComplianceFieldLabel from './helpers/buildComplianceFieldLabel';
import AggregatedBooleanField from './aggregated/fields/AggregatedBooleanField';
import AggregatedBoundScalarField from './aggregated/fields/AggregatedBoundScalarField';
import { aggregatedPricingFieldsPropType } from './aggregated/helpers/aggregatedFieldPropTypes';
import { isAggregatedFieldMultiple } from './aggregated/helpers/aggregatedFieldHelpers';
import { resolveAggregatedFieldTooltip } from './aggregated/helpers/resolveAggregatedFieldTooltip';
import s from './styles.module.scss';

const l10n = localization.PRODUCT_INFO;

const Pricing = ({
  price,
  compareAtPrice,
  taxable,
  unitCost,
  diff,
  detailsPanelVisibility,
  toggleVisibility,
  blockName,
  blockTitle,
  onChangePrice,
  onChangeCompareAtPrice,
  onChangeTaxable,
  onChangeUnitCost,
  hidePriceFields,
  hideUnitCost,
  readOnly,
  readOnlyTooltip,
  aggregatedFields,
}) => {
  const saveTaxable = useCallback(() => {
    if (readOnly) return;

    onChangeTaxable(!taxable);
  }, [taxable, onChangeTaxable, readOnly]);

  const priceMultiple = isAggregatedFieldMultiple(aggregatedFields?.price);
  const compareAtPriceMultiple = isAggregatedFieldMultiple(aggregatedFields?.compareAtPrice);
  const taxableMultiple = isAggregatedFieldMultiple(aggregatedFields?.taxable);
  const costMultiple = isAggregatedFieldMultiple(aggregatedFields?.cost);
  const taxableLabel = buildComplianceFieldLabel({
    label: l10n.chargeTax,
    diff: diff?.taxable,
    dimLabel: readOnly,
  });

  return (
    <DetailsBlock
      dataQa="productPricing"
      detailsPanelVisibility={detailsPanelVisibility}
      toggleVisibility={toggleVisibility}
      blockName={blockName}
      blockTitle={blockTitle}
    >
      <If condition={!hidePriceFields}>
        <div className={s.row}>
          <Choose>
            <When condition={priceMultiple}>
              <AggregatedBoundScalarField
                fieldKey="price"
                field={aggregatedFields.price}
                label={<Tooltip content={diff?.price ? 'Unpublished changes' : null} placement="top"><span className={diff?.price ? s.diffDot : undefined}>Price</span></Tooltip>}
                readOnly={readOnly}
                readOnlyTooltip={readOnlyTooltip}
              />
            </When>
            <Otherwise>
              <PricingField
                value={price}
                label={<Tooltip content={diff?.price ? 'Unpublished changes' : null} placement="top"><span className={diff?.price ? s.diffDot : undefined}>Price</span></Tooltip>}
                readOnly={readOnly}
                readOnlyTooltip={readOnlyTooltip}
                tooltip={resolveAggregatedFieldTooltip({
                  field: aggregatedFields?.price,
                  layerReadOnly: readOnly,
                  layerReadOnlyTooltip: readOnlyTooltip,
                  canSet: !readOnly,
                  canClear: false,
                })}
                onCommit={onChangePrice}
              />
            </Otherwise>
          </Choose>
          <Choose>
            <When condition={compareAtPriceMultiple}>
              <AggregatedBoundScalarField
                fieldKey="compareAtPrice"
                field={aggregatedFields.compareAtPrice}
                label={<Tooltip content={diff?.compareAtPrice ? 'Unpublished changes' : null} placement="top"><span className={diff?.compareAtPrice ? s.diffDot : undefined}>Compare-at price</span></Tooltip>}
                readOnly={readOnly}
                readOnlyTooltip={readOnlyTooltip}
              />
            </When>
            <Otherwise>
              <PricingField
                value={compareAtPrice}
                label={<Tooltip content={diff?.compareAtPrice ? 'Unpublished changes' : null} placement="top"><span className={diff?.compareAtPrice ? s.diffDot : undefined}>Compare-at price</span></Tooltip>}
                readOnly={readOnly}
                readOnlyTooltip={readOnlyTooltip}
                tooltip={resolveAggregatedFieldTooltip({
                  field: aggregatedFields?.compareAtPrice,
                  layerReadOnly: readOnly,
                  layerReadOnlyTooltip: readOnlyTooltip,
                  canSet: !readOnly,
                  canClear: false,
                })}
                clearable
                onCommit={onChangeCompareAtPrice}
              />
            </Otherwise>
          </Choose>
        </div>
      </If>
      <Choose>
        <When condition={taxableMultiple}>
          <AggregatedBooleanField
            fieldKey="taxable"
            field={aggregatedFields.taxable}
            label={taxableLabel}
            dialogLabel={l10n.chargeTax}
            readOnly={readOnly}
            readOnlyTooltip={readOnlyTooltip}
          />
        </When>
        <Otherwise>
          <ReadOnlyFieldTooltip tooltip={readOnly ? readOnlyTooltip : null}>
            <div className={s.row}>
              <InputControlLabel
                control="checkbox"
                label={taxableLabel}
                value={taxable}
                disabled={readOnly}
                onChange={saveTaxable}
              />
            </div>
          </ReadOnlyFieldTooltip>
        </Otherwise>
      </Choose>
      <If condition={!hideUnitCost}>
        <div className={s.hr} />
        <div className={cn(s.row, s.fullWidthRow)}>
          <Choose>
            <When condition={costMultiple}>
              <AggregatedBoundScalarField
                fieldKey="cost"
                field={aggregatedFields.cost}
                label={<Tooltip content={diff?.unitCost ? 'Unpublished changes' : null} placement="top"><span className={diff?.unitCost ? s.diffDot : undefined}>Cost per item</span></Tooltip>}
                readOnly={readOnly}
                readOnlyTooltip={readOnlyTooltip}
              />
            </When>
            <Otherwise>
              <PricingField
                value={unitCost?.amount ?? ''}
                label={<Tooltip content={diff?.unitCost ? 'Unpublished changes' : null} placement="top"><span className={diff?.unitCost ? s.diffDot : undefined}>Cost per item</span></Tooltip>}
                readOnly={readOnly}
                readOnlyTooltip={readOnlyTooltip}
                tooltip={resolveAggregatedFieldTooltip({
                  field: aggregatedFields?.cost,
                  layerReadOnly: readOnly,
                  layerReadOnlyTooltip: readOnlyTooltip,
                  canSet: !readOnly,
                  canClear: false,
                })}
                clearable
                onCommit={onChangeUnitCost}
              />
            </Otherwise>
          </Choose>
        </div>
      </If>
    </DetailsBlock>
  );
};

Pricing.propTypes = {
  price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  compareAtPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  taxable: PropTypes.bool,
  unitCost: PropTypes.shape({ amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) }),
  diff: PropTypes.shape({
    price: PropTypes.bool,
    compareAtPrice: PropTypes.bool,
    taxable: PropTypes.bool,
    unitCost: PropTypes.bool,
  }),
  detailsPanelVisibility: PropTypes.objectOf(PropTypes.bool).isRequired,
  toggleVisibility: PropTypes.func.isRequired,
  blockName: PropTypes.string.isRequired,
  blockTitle: PropTypes.node.isRequired,
  readOnly: PropTypes.bool,
  readOnlyTooltip: PropTypes.string,
  hidePriceFields: PropTypes.bool,
  hideUnitCost: PropTypes.bool,
  aggregatedFields: aggregatedPricingFieldsPropType,
  onChangePrice: PropTypes.func.isRequired,
  onChangeCompareAtPrice: PropTypes.func.isRequired,
  onChangeTaxable: PropTypes.func.isRequired,
  onChangeUnitCost: PropTypes.func.isRequired,
};

Pricing.defaultProps = {
  price: '',
  compareAtPrice: '',
  taxable: false,
  unitCost: null,
  diff: {},
  readOnly: false,
  readOnlyTooltip: null,
  hidePriceFields: false,
  hideUnitCost: false,
  aggregatedFields: null,
};

export default memo(Pricing);
